import { DocumentationIcon, HelpIcon, InfoIcon, MailIcon } from '@assets/icons';
import { DiscordIcon } from '@assets/logos';
import { ExternalLink } from '@components/link';
import { useContactUsModal, useModal } from '@components/modal';
import { Typography } from '@components/typography';
import { MenuItem, Stack } from '@mui/material';
import { useOrganizationContext } from '@navigation/OrganizationContext';
import { GettingStartedModal } from '@subsets/landing-page';
import posthog from 'posthog-js';
import { useState } from 'react';
import { HeaderMenu } from './HeaderMenu';

const ContactUsMenuItem = ({ onClick }: { onClick: VoidFunction }) => {
  return (
    <MenuItem onClick={onClick} sx={{ display: 'flex', gap: 2 }}>
      <MailIcon size={20} />
      <Typography variant="body2" color="text.primary">
        Contact Us
      </Typography>
    </MenuItem>
  );
};

const DocumentationMenuItem = ({
  onClick,
  documentationUrl,
}: {
  onClick: VoidFunction;
  documentationUrl: string;
}) => {
  return (
    <ExternalLink unstyled href={documentationUrl}>
      <MenuItem onClick={onClick}>
        <Stack direction="row" gap={2}>
          <DocumentationIcon size={20} />
          <Typography variant="body2" color="text.primary">
            Documentation
          </Typography>
        </Stack>
      </MenuItem>
    </ExternalLink>
  );
};

const IntroModalMenuItem = ({ onClick, studio }: { onClick: VoidFunction; studio: string }) => {
  return (
    <MenuItem onClick={onClick} sx={{ display: 'flex', gap: 2 }}>
      <InfoIcon size={20} />
      <Typography variant="body2" color="text.primary">
        Intro to {studio}
      </Typography>
    </MenuItem>
  );
};

const DiscordMenuItem = () => {
  return (
    <ExternalLink unstyled href="https://discord.com/invite/9khnYfPpuP">
      <MenuItem sx={{ display: 'flex', gap: 2 }}>
        <DiscordIcon width={20} />
        <Typography variant="body2" color="text.primary">
          Discord
        </Typography>
      </MenuItem>
    </ExternalLink>
  );
};

export const Help = () => {
  const { organization } = useOrganizationContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClose = () => setAnchorEl(null);

  const { showModal } = useContactUsModal({ onModalOpen: handleClose });
  const handleContactUsClick = () => {
    posthog.capture('user_contact-us_intent', { location: 'help-menu' });
    showModal();
  };

  const handleDocumentationClick = () => {
    posthog.capture(
      `user_clicked_help-${organization?.serviceOnly ? 'agent-studio' : 'synthetic-data-studio'}-documentation`,
    );
    handleClose();
  };

  const { showModal: showIntroModal } = useModal({
    component: GettingStartedModal,
    onModalOpen: handleClose,
    modalProps: { sx: { py: 12, transition: 'all .5s' } },
  });
  const handleIntroModalClick = () => {
    if (organization?.serviceOnly) {
      posthog.capture('user_clicked_help-agent-studio-intro_modal');
      showIntroModal({ componentProps: { agentStudio: true } });
    } else {
      posthog.capture('user_clicked_help-synthetic-data-studio-intro_modal');
      showIntroModal();
    }
  };

  const menuItems = [
    <ContactUsMenuItem key="contact-us" onClick={handleContactUsClick} />,
    <DocumentationMenuItem
      key="documentation"
      onClick={handleDocumentationClick}
      documentationUrl={`https://docs.${process.env.GATSBY_ENV === 'prod' ? '' : `${process.env.GATSBY_ENV}.`}rendered.ai/${organization?.serviceOnly ? 'agent-studio' : 'synthetic-data-studio'}/`}
    />,
  ];

  if (organization?.serviceOnly) {
    menuItems.push(<DiscordMenuItem key="discord" />);
  }

  menuItems.push(
    <IntroModalMenuItem
      key="intro-modal"
      onClick={handleIntroModalClick}
      studio={organization?.serviceOnly ? 'Agent studio' : 'Synthetic data studio'}
    />,
  );

  return (
    <HeaderMenu icon={HelpIcon} anchorEl={anchorEl} setAnchorEl={setAnchorEl} ariaLabel="Help menu">
      {menuItems}
    </HeaderMenu>
  );
};
